import { EXO_SKELETON_EVENT_NAMES } from '../consts';
import {
  createPostMessageEventHandler,
  ExoSkeletonRuntimeDataEvent,
  hasExoSkeletonListenerInitialized,
  sendRuntimeDataEvent,
} from '../runtime';

export interface ImportRemoteMessageData<
  METADATA extends Record<string, unknown>,
> {
  scope: string;
  metadata: METADATA;
}

const IMPORT_REMOTE_MESSAGE_EVENT_NAME =
  EXO_SKELETON_EVENT_NAMES.IMPORT_REMOTE_MESSAGE_EVENT_NAME;

type RuntimeImportRemoteEvent<METADATA extends Record<string, unknown>> =
  ExoSkeletonRuntimeDataEvent<
    typeof IMPORT_REMOTE_MESSAGE_EVENT_NAME,
    ImportRemoteMessageData<METADATA>
  >;

function isImportRemoteDataEvent<METADATA extends Record<string, unknown>>(
  data: unknown,
): data is RuntimeImportRemoteEvent<METADATA> {
  return (
    (data as RuntimeImportRemoteEvent<METADATA>)?.type ===
    IMPORT_REMOTE_MESSAGE_EVENT_NAME
  );
}

interface CreateImportRemoteListenerParams<T extends Record<string, unknown>> {
  onImportRemote: <METADATA extends T>(
    event: RuntimeImportRemoteEvent<METADATA>,
  ) => void;
}

/**
 * Used to create a host for the ExoSkeleton.
 */
export function createImportRemoteListener<
  METADATA extends Record<string, unknown>,
>({ onImportRemote }: CreateImportRemoteListenerParams<METADATA>) {
  return createPostMessageEventHandler(
    IMPORT_REMOTE_MESSAGE_EVENT_NAME,
    function ({ data }: MessageEvent<unknown>) {
      if (isImportRemoteDataEvent<METADATA>(data)) {
        onImportRemote(data);
      }
    },
  );
}

export function sendImportRemoteRuntimeEvent<
  METADATA extends Record<string, unknown>,
>({ scope, metadata }: ImportRemoteMessageData<METADATA>) {
  if (!hasExoSkeletonListenerInitialized(IMPORT_REMOTE_MESSAGE_EVENT_NAME)) {
    console.warn(
      '"sendImportRemoteRuntimeEvent" was called, but the ExoSkeleton host did not initialize the "createImportRemoteListener". Data sent:',
      { scope, metadata },
    );
  }

  return sendRuntimeDataEvent<
    typeof IMPORT_REMOTE_MESSAGE_EVENT_NAME,
    ImportRemoteMessageData<METADATA>
  >(IMPORT_REMOTE_MESSAGE_EVENT_NAME, {
    scope,
    metadata,
  });
}
